import { AlertTriangle, Check, Plus, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { TextInput, showToast } from '@my/ui';
import {
  resolveOrderReferenceLabel,
  type OrderReferenceExtra,
} from '@/utils/orderReferenceLabels';

interface ReferencesPanelProps {
  /**
   * Positional labels — index `i` is the label for `Reference${i+1}`.
   * Always at least 2 entries; fallback labels are applied per-slot.
   */
  labels: string[];
  /** Per-slot values, same length/indexing as `labels`. Fully controlled. */
  values: string[];
  /** Replace the slot value at `index`. */
  onChange: (index: number, next: string) => void;
  /** Free-form per-order extras — operator-added label/value pairs. */
  extras: OrderReferenceExtra[];
  /** Replace the extras array wholesale (add / remove / value edit). */
  onExtrasChange: (next: OrderReferenceExtra[]) => void;
  /**
   * 1-based slots THIS customer insists on, from
   * `Party.tmsRequiredOrderReferencesJson`.
   *
   * <p>
   * ADVISORY, and the styling says so: amber rather than the red this form uses
   * for a value it will refuse to save. Nothing here blocks the save or the
   * status move — the workflow policy is where hard gates live, and it applies
   * to every customer alike. Marking the field is the whole point: an operator
   * who knows this customer bounces invoices without their PO can fill it in
   * now instead of finding out at invoicing.
   * </p>
   */
  requiredSlots?: readonly number[];
}

/**
 * Order-level reference fields. Two kinds coexist:
 *
 * 1. **Org-configured slots** — positional, driven by the org's
 *    `tmsOrderReferenceLabelsJson`. Slots 1–4 map to dedicated
 *    `Order.Reference${i+1}` columns; slots 5+ live in
 *    `AdditionalReferencesJson`.
 *
 * 2. **Order-specific extras** — free-form `{ label, value }` pairs the
 *    operator adds via the "+ Add reference" affordance. Also persisted in
 *    `AdditionalReferencesJson`, distinguished by key shape.
 */
export function ReferencesPanel({
  labels,
  values,
  onChange,
  extras,
  onExtrasChange,
  requiredSlots = [],
}: ReferencesPanelProps) {
  const slotCount = Math.max(labels.length, values.length);

  // Which required slots are still blank. A slot the customer demands but the
  // organisation no longer configures is deliberately NOT counted: the field is
  // not on screen, so warning about it would name something the operator cannot
  // fill in from here.
  const missingRequired = useMemo(() => {
    const out: number[] = [];
    for (const slot of requiredSlots) {
      if (slot < 1 || slot > slotCount) continue;
      if (!(values[slot - 1] ?? '').trim()) out.push(slot);
    }
    return out;
  }, [requiredSlots, slotCount, values]);

  const [isAdding, setIsAdding] = useState(false);
  const [draftLabel, setDraftLabel] = useState('');

  // Case-insensitive set of taken labels — rejects duplicate adds.
  const takenLabels = useMemo(() => {
    const set = new Set<string>();
    for (let i = 0; i < slotCount; i++) {
      set.add(resolveOrderReferenceLabel(labels, i).toLowerCase());
    }
    for (const e of extras) {
      const v = e.label.trim().toLowerCase();
      if (v) set.add(v);
    }
    return set;
  }, [labels, slotCount, extras]);

  const handleConfirmAdd = () => {
    const label = draftLabel.trim();
    if (!label) return;
    // Block the slot-pattern so a free-form extra can't shadow a positional entry.
    if (/^Reference\d+$/i.test(label)) {
      showToast({
        type: 'error',
        message:
          'Use the org-level Order Reference Labels editor to name slots like "Reference 5".',
      });
      return;
    }
    if (takenLabels.has(label.toLowerCase())) {
      showToast({ type: 'error', message: `A reference named "${label}" already exists.` });
      return;
    }
    onExtrasChange([...extras, { label, value: '' }]);
    setDraftLabel('');
    setIsAdding(false);
  };

  const handleCancelAdd = () => {
    setDraftLabel('');
    setIsAdding(false);
  };

  const handleExtraValueChange = (index: number, next: string) => {
    const out = extras.slice();
    const target = out[index];
    if (!target) return;
    out[index] = { ...target, value: next };
    onExtrasChange(out);
  };

  const handleExtraRemove = (index: number) => {
    onExtrasChange(extras.filter((_, i) => i !== index));
  };

  return (
    <section className="rounded-md border border-slate-200 bg-white">
      <header className="flex items-center justify-between gap-2 border-b border-slate-200 px-3 py-2">
        <h2 className="text-sm font-semibold text-slate-900">References</h2>
        {/* One count at the top, so the state is readable without reading every
            field — the panel sits beside Documents and an operator scans it. */}
        {missingRequired.length > 0 && (
          <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700 ring-1 ring-amber-200">
            <AlertTriangle className="h-3 w-3" />
            {missingRequired.length === 1
              ? '1 required reference missing'
              : `${missingRequired.length} required references missing`}
          </span>
        )}
      </header>

      <div className="px-3 py-2">
        {/*
          38px is derived, not picked. The reference fields have to finish on the
          same vertical line as the Customer and Cargo boxes above, and those sit
          in a 4-column grid across the full width while these sit in a 2-column
          grid inside a half-width card. Writing both right edges as a function
          of the page width W (outer card gap 8, card padding 12, borders 1) and
          solving for this gap `g`:

              (W - 38) / 4 - 11  =  (W - 10) / 4 + 1 - g / 2   →   g = 38

          The W terms cancel, so the alignment holds at every viewport rather
          than at the one it was eyeballed on.
        */}
        {/* gap-y-3, not the page's usual gap-y-2: these inputs carry FLOATING
            labels that sit on their own top border, so 8px left the "Manifest
            Ref" label almost touching the box above it. Only the row gap moves
            — the column gap stays at the derived 38px, and nothing outside this
            grid changes. */}
        <div className="grid grid-cols-1 gap-y-3 sm:grid-cols-2 sm:gap-x-[38px]">
          {Array.from({ length: slotCount }).map((_, idx) => {
            const label = resolveOrderReferenceLabel(labels, idx);
            const slot = idx + 1;
            const isRequired = requiredSlots.includes(slot);
            const isMissing = isRequired && !(values[idx] ?? '').trim();
            return (
              <TextInput
                key={`reference${slot}`}
                label={
                  isRequired ? (
                    <>
                      {label}{' '}
                      {/* Amber, not the red asterisk this app uses for a field
                          the form will refuse to save without. The customer
                          requires it; the software does not. */}
                      <span className="text-amber-600" title="Required by this customer">
                        *
                      </span>
                    </>
                  ) : (
                    label
                  )
                }
                name={`reference${slot}`}
                value={values[idx] ?? ''}
                maxLength={64}
                // `cn` in TextInput runs through tailwind-merge, so this border
                // beats the component's own without !important.
                className={isMissing ? 'border-amber-400 focus:ring-amber-500' : undefined}
                helperText={
                  isMissing ? (
                    <span className="text-amber-700">Required by this customer</span>
                  ) : undefined
                }
                onChange={(e) => onChange(idx, e.target.value)}
              />
            );
          })}

          {/* Free-form extras — label locked after add, value editable inline.
              The X only shows when value is empty to protect typed data. */}
          {extras.map((extra, idx) => {
            const isEmpty = extra.value.trim().length === 0;
            return (
              <div key={`extra-${idx}-${extra.label}`} className="flex items-center gap-1">
                <div className="flex-1">
                  <TextInput
                    label={extra.label}
                    name={`extra-${idx}`}
                    value={extra.value}
                    maxLength={64}
                    onChange={(e) => handleExtraValueChange(idx, e.target.value)}
                  />
                </div>
                {isEmpty && (
                  <button
                    type="button"
                    onClick={() => handleExtraRemove(idx)}
                    aria-label={`Remove ${extra.label}`}
                    title="Remove"
                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-sm text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {isAdding ? (
          <div className="mt-3 flex items-center gap-2 rounded-md border border-purple-200 bg-purple-50/40 p-2">
            <div className="flex-1">
              <TextInput
                label="Reference name"
                name="new-reference-label"
                autoFocus
                value={draftLabel}
                maxLength={64}
                onChange={(e) => setDraftLabel(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleConfirmAdd();
                  if (e.key === 'Escape') handleCancelAdd();
                }}
              />
            </div>
            <button
              type="button"
              onClick={handleCancelAdd}
              aria-label="Cancel"
              title="Cancel"
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-sm text-slate-500 hover:bg-slate-100"
            >
              <X className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={handleConfirmAdd}
              aria-label="Add reference"
              title="Add reference"
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-sm text-emerald-600 hover:bg-emerald-50"
            >
              <Check className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setIsAdding(true)}
            className="mt-2 flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-xs font-medium text-purple-600 hover:bg-purple-50"
          >
            <Plus className="h-3.5 w-3.5" />
            Add reference
          </button>
        )}
      </div>
    </section>
  );
}
